import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useProvider } from '../hooks/useProvider';
import { useCloutReviewsEnabled } from '../hooks/useCloutReviewsEnabled';
import { toSafeHref } from '../utils/url';
import NotAvailable from '../components/Common/NotAvailable';
import ProviderRating from '../components/provider/ProviderRating';
import GoogleRatingPanel from '../components/provider/GoogleRatingPanel';
import ProviderReviews from '../components/reviews/ProviderReviews';

// Import local images
import cloutLogo from '../assets/logos/clout-logo.png';

// Icons
const ArrowLeftIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15 18L9 12L15 6"
      stroke="#1f1f1f"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const StarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8 1.33334L10.06 5.50668L14.6667 6.18001L11.3333 9.42668L12.12 14.0133L8 11.8467L3.88 14.0133L4.66667 9.42668L1.33334 6.18001L5.94 5.50668L8 1.33334Z"
      fill="#c6a664"
      stroke="#c6a664"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const BriefcaseIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.3333 4.66666H2.66667C1.93029 4.66666 1.33334 5.26361 1.33334 5.99999V12.6667C1.33334 13.403 1.93029 14 2.66667 14H13.3333C14.0697 14 14.6667 13.403 14.6667 12.6667V5.99999C14.6667 5.26361 14.0697 4.66666 13.3333 4.66666Z"
      stroke="#c6a664"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.6667 14V3.33333C10.6667 2.97971 10.5262 2.64057 10.2761 2.39052C10.0261 2.14048 9.68696 2 9.33334 2H6.66667C6.31305 2 5.97391 2.14048 5.7239 2.39052C5.47381 2.64057 5.33334 2.97971 5.33334 3.33333V14"
      stroke="#c6a664"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LocationIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M14 6.66668C14 11.3333 8 15.3333 8 15.3333C8 15.3333 2 11.3333 2 6.66668C2 5.07538 2.63214 3.54926 3.75736 2.42404C4.88258 1.29882 6.4087 0.666687 8 0.666687C9.5913 0.666687 11.1174 1.29882 12.2426 2.42404C13.3679 3.54926 14 5.07538 14 6.66668Z"
      stroke="#c6a664"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M8 8.66668C9.10457 8.66668 10 7.77125 10 6.66668C10 5.56211 9.10457 4.66668 8 4.66668C6.89543 4.66668 6 5.56211 6 6.66668C6 7.77125 6.89543 8.66668 8 8.66668Z"
      stroke="#c6a664"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const VerifiedIcon = () => (
  <div className="bg-[#c6a664] rounded-full p-1 inline-flex">
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M10.5 3L4.5 9L1.5 6"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </div>
);

const PublicProviderProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { provider, loading, error } = useProvider(id);
  const cloutReviewsEnabled = useCloutReviewsEnabled();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fcfaf8] flex items-center justify-center">
        <div className="text-[#767472]">Loading provider profile...</div>
      </div>
    );
  }

  if (error || !provider) {
    return (
      <div className="min-h-screen bg-[#fcfaf8] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#767472] mb-4">Provider not found</p>
          <button onClick={() => navigate('/')} className="text-[#c6a664] hover:underline">
            Return to home
          </button>
        </div>
      </div>
    );
  }

  const websiteHref = toSafeHref(provider.website);

  return (
    <div className="min-h-screen bg-[#fcfaf8]">
      {/* Header */}
      <header className="bg-[#f8f6f2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 sm:py-4">
            {/* Back Button */}
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white/50 rounded-full transition-colors"
            >
              <ArrowLeftIcon />
            </button>

            {/* Logo */}
            <div className="flex items-center">
              <img src={cloutLogo} alt="CLOUT Aesthetics" className="h-10 sm:h-12 lg:h-14" />
            </div>

            {/* Sign In Button */}
            <button
              onClick={() => navigate('/signin')}
              className="px-3 sm:px-6 py-2 bg-[#c6a664] text-white font-['Open_Sans'] text-xs sm:text-sm font-semibold uppercase hover:bg-[#b89854] transition-colors"
            >
              Sign In
            </button>
          </div>
        </div>
      </header>

      {/* Profile Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 lg:p-8">
          {/* Profile Header */}
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:space-x-6">
              {/* Profile Image */}
              <div className="w-20 h-20 sm:w-24 sm:h-24 bg-[#f8f6f2] rounded-full overflow-hidden flex-shrink-0">
                {provider.user_profile_image_url ? (
                  <img
                    src={provider.user_profile_image_url}
                    alt={provider.business_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl sm:text-3xl font-['Ovo'] text-[#c6a664]">
                    {provider.business_name?.charAt(0).toUpperCase() || 'P'}
                  </div>
                )}
              </div>

              {/* Provider Info */}
              <div className="text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-2 sm:gap-3 mb-2">
                  <h1 className="font-['Ovo'] text-xl sm:text-2xl text-[#1f1f1f]">
                    {provider.business_name || 'Provider'}
                  </h1>
                  {provider.is_certified > 0 && <VerifiedIcon />}
                </div>

                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 sm:gap-4 text-xs sm:text-sm text-[#767472] mb-3">
                  <div className="flex items-center gap-1">
                    <BriefcaseIcon />
                    <span>
                      {provider.specialties?.length ? (
                        provider.specialties.join(', ')
                      ) : (
                        <NotAvailable />
                      )}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <LocationIcon />
                    <span>
                      {[provider.city, provider.state].filter(Boolean).join(', ') || (
                        <NotAvailable />
                      )}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-center sm:justify-start gap-2">
                  <span className="font-['Open_Sans'] text-xs sm:text-sm text-[#767472]">
                    CLOUT Score:
                  </span>
                  <div className="flex items-center gap-1">
                    <StarIcon />
                    <span className="font-['Open_Sans'] text-base sm:text-lg font-semibold text-[#1f1f1f]">
                      {provider.clout_score || 0}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-center sm:justify-start gap-2 mt-1 font-['Open_Sans'] text-xs sm:text-sm text-[#767472]">
                  <span>Rating:</span>
                  <ProviderRating
                    avgRating={provider.avg_rating}
                    reviewCount={provider.review_count}
                  />
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => navigate('/signin')}
              className="w-full sm:w-auto px-4 sm:px-6 py-3 bg-[#1f1f1f] text-white font-['Ovo'] text-sm uppercase hover:bg-[#333333] transition-colors"
            >
              Sign In to Connect
            </button>
          </div>

          {/* Live Google rating, only if the provider linked their listing */}
          {provider.google_place_id && (
            <div className="mb-6 sm:mb-8 max-w-md empty:hidden">
              <GoogleRatingPanel providerId={provider.id} />
            </div>
          )}

          {/* About Section */}
          <div className="mb-6 sm:mb-8">
            <h2 className="font-['Ovo'] text-lg sm:text-xl text-[#1f1f1f] mb-3 sm:mb-4">About</h2>
            <p className="font-['Open_Sans'] text-sm sm:text-base text-[#767472] whitespace-pre-wrap">
              {provider.bio || <NotAvailable />}
            </p>
          </div>

          {/* Specialties */}
          <div className="mb-6 sm:mb-8">
            <h2 className="font-['Ovo'] text-lg sm:text-xl text-[#1f1f1f] mb-3 sm:mb-4">
              Specialties
            </h2>
            {provider.specialties?.length ? (
              <div className="flex flex-wrap gap-2">
                {provider.specialties.map((specialty, index) => (
                  <span
                    key={index}
                    className="px-2 sm:px-3 py-1 bg-[#f8f6f2] text-[#1f1f1f] font-['Open_Sans'] text-xs sm:text-sm rounded-full"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            ) : (
              <NotAvailable className="text-sm" />
            )}
          </div>

          {/* Reviews clients left on CLOUT, once they're open */}
          {cloutReviewsEnabled && (
            <ProviderReviews
              providerId={provider.id}
              providerName={provider.business_name || 'this provider'}
            />
          )}

          {/* Contact Information */}
          <div className="border-t border-[#eaeaea] pt-6 sm:pt-8">
            <h2 className="font-['Ovo'] text-lg sm:text-xl text-[#1f1f1f] mb-3 sm:mb-4">
              Contact Information
            </h2>
            <div className="space-y-2 text-[#767472] font-['Open_Sans'] text-xs sm:text-sm">
              <p>Phone: {provider.phone || <NotAvailable />}</p>
              {provider.user_email && <p>Email: {provider.user_email}</p>}
              <p>
                Website:{' '}
                {websiteHref ? (
                  <a
                    href={websiteHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#c6a664] hover:underline"
                  >
                    {provider.website}
                  </a>
                ) : (
                  <NotAvailable />
                )}
              </p>
              <p className="text-[#c6a664] mt-4">
                Sign in to see full contact details and connect with this provider
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PublicProviderProfile;
